import React, { useEffect, useState, createContext, useContext, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "../context/AuthContext";

const FavoritesCtx = createContext({
  favorites: new Set(),
  toggle: async () => {},
  loading: false,
  count: 0,
});

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const r = await api.get("/favorites");
      setIds(new Set((r.data?.items || []).map((v) => v.id)));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const toggle = useCallback(
    async (vehicleId) => {
      if (!vehicleId) return;
      const isFav = ids.has(vehicleId);
      // Optimistic
      setIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.delete(vehicleId);
        else next.add(vehicleId);
        return next;
      });
      try {
        if (isFav) {
          await api.delete(`/favorites/${vehicleId}`);
        } else {
          await api.post(`/favorites`, { vehicle_id: vehicleId });
        }
      } catch {
        // rollback
        setIds((prev) => {
          const next = new Set(prev);
          if (isFav) next.add(vehicleId);
          else next.delete(vehicleId);
          return next;
        });
      }
    },
    [ids]
  );

  return (
    <FavoritesCtx.Provider value={{ favorites: ids, toggle, loading, count: ids.size, refresh }}>
      {children}
    </FavoritesCtx.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesCtx);
}
